import { JSX, useCallback, useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { Grid } from 'semantic-ui-react'

import { useFormFlow, useSetScreen } from '@/components/form-flow'
import {
  DELETE_TAX_QUESTIONNAIRE_RESPONSES_KEY,
  FETCH_USER_TAX_QUESTIONNAIRE_KEY,
  UPSERT_USER_TAX_QUESTIONNAIRE_KEY,
  fetchUserTaxQuestionnaire,
  postUpdateTaxQuestionnaireResponses,
} from '../../taxChecklistQuestion.actions'
import { selectCurrentAnnualTaxYear } from '@/features/admin/annual-tax-details/annualTaxDetails.selector'
import { useReselector, useScrollRef } from '@/utils/sharedHooks'
import { TaxChecklistResponse } from '@/features/taxes/annual-taxes/tax-checklist/taxChecklistQuestion.slice'
import {
  invalidateFetch,
  selectErrorsForKeys,
  selectIsFetchingForKeys,
} from '@/reducers/fetch'
import { Alert, GridRowColumn, Loader } from '@/componentsV1/BaseComponents'
import IncomeAndLiabilitiesPanel from './IncomeAndLiabilitiesPanel'
import {
  FETCH_ANNUAL_TAX_FILING_FORMS_KEY,
  fetchAnnualTaxFilingFormsIfNeeded,
} from '@/features/taxes/annual-taxes/annualTaxFilingForms.slice'
import RentalPropertyPanel from './RentalPropertyPanel'
import RetirementContributedPanel from './RetirementContributedPanel'
import IncomeAndLiabilitiesReviewPanel from './ReviewPanel'
import { selectTaxListQuestion } from '@/features/taxes/annual-taxes/tax-checklist/taxChecklist.selectors'
import { TaxListQuestionId } from '@/features/taxes/annual-taxes/tax-checklist/service'
import {
  FETCH_ALL_ANNUAL_TAX_DETAILS_KEY,
  fetchAllAnnualTaxDetailsIfNeeded,
} from '@/features/admin/annual-tax-details/annualTaxDetails.slice'
import { PageHeader } from '@/components/PageHeader'
import InvestmentsPanel from './InvestmentsPanel'
import LessCommonScenariosPanel from './LessCommonScenariosPanel'
import useProgressSteps, {
  StepUpdates,
} from '@/features/taxes/annual-taxes/tax-checklist/shared/review-steps-and-progresses/useProgressSteps'
import { NON_THERAPY_INCOME_AND_LIABILITIES } from '@/features/taxes/annual-taxes/tax-checklist/shared/review-steps-and-progresses/stepProgress.helpers'
import { filterNulls, useAppDispatch } from '@/utils/typeHelpers'
import { selectTqFormIsReadOnly } from '@/features/taxes/annual-taxes/annualTaxFilings.selector'
import { fetchUserDocuments } from '@/features/user/your-practice/documents/lib/userDocuments.slice'
import { fetchTaxUserDocumentsIfNeeded } from '@/features/taxes/annual-taxes/taxUserDocuments.slice'
import { fetchUserDocumentCategoriesIfNeeded } from '@/features/admin/user-document-categories/userDocumentCategories.slice'
import { OutsideW2Panel } from '@/features/taxes/annual-taxes/tax-checklist/form1040/income-and-liabilities/OutsideW2Panel'

export enum INCOME_LIABILITIES_SCREENS {
  incomeAndLiabilitiesIntro = 'income-liabilities-intro',
  outsideW2s = 'outside-w2s',
  rentalProperty = 'rental-property',
  investments = 'investments',
  retirementContributed = 'retirement-contributed',
  lessCommonScenarios = 'less-common-scenarios',
  review = 'review',
}

export interface IncomeAndLiabilitiesStepProps {
  goToNextStep: (
    data: Partial<TaxChecklistResponse | undefined>[] | null,
    newScreen: INCOME_LIABILITIES_SCREENS | null,
    stepUpdates?: StepUpdates,
    getNextScreen?: () => INCOME_LIABILITIES_SCREENS
  ) => Promise<boolean>
  goBack: (newScreen: INCOME_LIABILITIES_SCREENS | null) => void
  nextScreen?: INCOME_LIABILITIES_SCREENS | null
  previousScreen?: INCOME_LIABILITIES_SCREENS | null
}

const IncomeAndLiabilities = () => {
  const dispatch = useAppDispatch()
  const { formId } = useParams()
  const readOnly = useReselector(selectTqFormIsReadOnly, formId)
  const backLink = '/taxes/annual/tax_checklist/'
  const { setScreen, currentScreen } = useSetScreen({
    backLink,
    reviewScreen: INCOME_LIABILITIES_SCREENS.review,
  })
  const { scrollRef, scrollToRef } = useScrollRef()

  const { updateProgressData, fetchKeys: progressFetchKeys } = useProgressSteps(
    { steps: NON_THERAPY_INCOME_AND_LIABILITIES }
  )

  const taxYear = useReselector(selectCurrentAnnualTaxYear)
  const updateError = useReselector(selectErrorsForKeys, [
    UPSERT_USER_TAX_QUESTIONNAIRE_KEY,
    DELETE_TAX_QUESTIONNAIRE_RESPONSES_KEY,
    ...progressFetchKeys,
  ])

  const isFetching = useReselector(selectIsFetchingForKeys, [
    FETCH_ANNUAL_TAX_FILING_FORMS_KEY,
    FETCH_ALL_ANNUAL_TAX_DETAILS_KEY,
    FETCH_USER_TAX_QUESTIONNAIRE_KEY(taxYear),
    ...progressFetchKeys,
  ])

  const saveFormData = useCallback(
    async (
      data: Partial<TaxChecklistResponse | undefined>[] | null,
      newScreen: INCOME_LIABILITIES_SCREENS | null,
      stepUpdates: StepUpdates = {}
    ) => {
      dispatch(invalidateFetch(UPSERT_USER_TAX_QUESTIONNAIRE_KEY))
      dispatch(invalidateFetch(DELETE_TAX_QUESTIONNAIRE_RESPONSES_KEY))

      const parsedData = data && filterNulls(data)

      if (parsedData?.length) {
        const res = await dispatch(
          postUpdateTaxQuestionnaireResponses(parsedData)
        )
        if (res) {
          const progressUpdatesSuccessful =
            await updateProgressData(stepUpdates)
          if (!progressUpdatesSuccessful) {
            return false
          }
          setScreen(newScreen)
          return true
        }
      } else {
        const progressUpdatesSuccessful = await updateProgressData(stepUpdates)
        if (!progressUpdatesSuccessful) {
          return false
        }
        setScreen(newScreen)
        return true
      }
      return false
    },
    [dispatch, setScreen, updateProgressData]
  )

  const props = useMemo(() => {
    return {
      goToNextStep: saveFormData,
      goBack: setScreen,
    }
  }, [saveFormData, setScreen])

  const questionOutsideW2s = useReselector(
    selectTaxListQuestion,
    TaxListQuestionId.has_outside_w2s,
    taxYear
  )

  const questionRentalProperty = useReselector(
    selectTaxListQuestion,
    TaxListQuestionId.rental_property_owned,
    taxYear
  )
  const questionInvestments = useReselector(
    selectTaxListQuestion,
    TaxListQuestionId.investments_owned,
    taxYear
  )
  const questionRetirementContributed = useReselector(
    selectTaxListQuestion,
    TaxListQuestionId.retirement_contributed,
    taxYear
  )

  const hasOtherW2s = questionOutsideW2s?.responses?.[0]?.value === true
  const hasRentals = questionRentalProperty?.responses?.[0]?.value === true
  const hasInvestments = questionInvestments?.responses?.[0]?.value === true
  const hasRetirementContributed =
    questionRetirementContributed?.responses?.[0]?.value === true

  const screenConfig = useMemo(() => {
    const newScreens: {
      component: () => JSX.Element
      screenName: INCOME_LIABILITIES_SCREENS
      progressText?: string
      shouldShow?: boolean
      step?: number
      order?: number
    }[] = [
      {
        component: () => <IncomeAndLiabilitiesPanel {...props} />,
        screenName: INCOME_LIABILITIES_SCREENS.incomeAndLiabilitiesIntro,
      },
      ...(hasOtherW2s
        ? [
            {
              component: () => <OutsideW2Panel {...props} />,
              screenName: INCOME_LIABILITIES_SCREENS.outsideW2s,
              progressText: 'W-2 income',
            },
          ]
        : []),
      ...(hasRentals
        ? [
            {
              component: () => <RentalPropertyPanel {...props} />,
              screenName: INCOME_LIABILITIES_SCREENS.rentalProperty,
              progressText: 'Rental Properties',
            },
          ]
        : []),
      ...(hasInvestments
        ? [
            {
              component: () => <InvestmentsPanel {...props} />,
              screenName: INCOME_LIABILITIES_SCREENS.investments,
              progressText: 'Investments',
            },
          ]
        : []),
      ...(hasRetirementContributed
        ? [
            {
              component: () => <RetirementContributedPanel {...props} />,
              screenName: INCOME_LIABILITIES_SCREENS.retirementContributed,
              progressText: 'Retirement',
            },
          ]
        : []),
      {
        component: () => <LessCommonScenariosPanel {...props} />,
        screenName: INCOME_LIABILITIES_SCREENS.lessCommonScenarios,
        progressText: 'Less Common Scenarios',
      },
      {
        component: () => <IncomeAndLiabilitiesReviewPanel {...props} />,
        screenName: INCOME_LIABILITIES_SCREENS.review,
        progressText: 'Review',
      },
    ]

    return newScreens.map((s, i) => ({
      ...s,
      step: i - 1,
    }))
  }, [hasInvestments, hasOtherW2s, hasRentals, hasRetirementContributed, props])

  const { progressBar, content } = useFormFlow({
    steps: screenConfig.flatMap((s) =>
      s.progressText ? [s.progressText] : []
    ),
    screens: screenConfig,
  })

  useEffect(() => {
    dispatch(fetchAnnualTaxFilingFormsIfNeeded())
    dispatch(fetchAllAnnualTaxDetailsIfNeeded())
    dispatch(fetchUserDocuments())
    dispatch(fetchTaxUserDocumentsIfNeeded())
    dispatch(fetchUserDocumentCategoriesIfNeeded())
  }, [dispatch])

  useEffect(() => {
    dispatch(fetchUserTaxQuestionnaire(taxYear))
  }, [taxYear, dispatch])

  useEffect(() => {
    if (!currentScreen) {
      setScreen(INCOME_LIABILITIES_SCREENS.incomeAndLiabilitiesIntro, true)
    }
  }, [setScreen, currentScreen])

  useEffect(() => {
    scrollToRef()
  }, [currentScreen, scrollToRef])

  return (
    <>
      <span ref={scrollRef} />
      <PageHeader
        backControl={
          readOnly
            ? { link: backLink, text: 'Back to Personal Tax Checklist' }
            : undefined
        }
        header={`${taxYear} Personal Tax Questionnaire: Income and Liabilities`}
      />
      <Grid>
        <Loader loading={isFetching} />
        {!readOnly &&
          !isFetching &&
          currentScreen !==
            INCOME_LIABILITIES_SCREENS.incomeAndLiabilitiesIntro && (
            <GridRowColumn>{progressBar}</GridRowColumn>
          )}
        {updateError.length > 0 && (
          <GridRowColumn>
            <Alert type="error">{updateError[0].message}</Alert>
          </GridRowColumn>
        )}
        <GridRowColumn>{content}</GridRowColumn>
      </Grid>
    </>
  )
}

export default IncomeAndLiabilities
